import { useEffect, useState } from "react";
import { Radio, Video, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VideoEmbed } from "@/components/VideoEmbed";

const GoLiveDialog = ({ open, setOpen, onStarted }) => {
  const [f, setF] = useState({ title: "", url: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      onStarted((await api.post("/live", f)).data);
      setOpen(false);
      toast.success("You're live! Members can see the banner now.");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white" data-testid="go-live-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Go live</DialogTitle></DialogHeader>
        <p className="text-sm text-[var(--hb-muted)]">
          Paste your Zoom meeting link (members tap to join) or a YouTube live link (plays right here in the app).
        </p>
        <form onSubmit={save} className="space-y-3">
          <input className="hb-input" placeholder="What's happening? e.g. Monday Power Hour" required value={f.title}
            onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="live-title-input" />
          <input className="hb-input" placeholder="https://zoom.us/j/... or https://youtube.com/live/..." required value={f.url}
            onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="live-url-input" />
          <button className="btn-primary w-full" data-testid="live-start-button"><Radio size={14} /> Start live</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const LiveBanner = () => {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [live, setLive] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const load = () => api.get("/live").then((r) => setLive(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const end = async () => {
    if (!window.confirm("End the live session for everyone?")) return;
    try {
      setLive((await api.delete("/live")).data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!live) return null;
  if (!live.active) {
    return isAdmin ? (
      <>
        <button className="btn-dark w-full" onClick={() => setOpen(true)} data-testid="go-live-open"><Video size={14} /> Go live</button>
        <GoLiveDialog open={open} setOpen={setOpen} onStarted={setLive} />
      </>
    ) : null;
  }
  return (
    <div className="hb-card overflow-hidden border-[var(--hb-ink)] rise" data-testid="live-banner">
      <div className="bg-[var(--hb-ink)] text-white px-5 py-3 flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-2 label-accent !text-[var(--hb-lime)]">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /> Live now
        </span>
        <span className="font-display text-lg flex-1 min-w-0 truncate" data-testid="live-title">{live.title}</span>
        {!live.youtube_id && (
          <a href={live.url} target="_blank" rel="noreferrer" className="btn-primary !py-2" data-testid="live-join-link">
            Join live <ExternalLink size={13} />
          </a>
        )}
        {isAdmin && <button className="btn-ghost !py-1.5" onClick={end} data-testid="live-end-button">End live</button>}
      </div>
      {live.youtube_id && (
        <div className="p-3 -mt-4"><VideoEmbed video={{ kind: "youtube", id: live.youtube_id }} testId="live-player" /></div>
      )}
    </div>
  );
};
